import { gt, gte, valid } from 'semver'

/** Date-based player/APK version: `YYYYMMDD-seq` (e.g. `20260318-1`). Matches API `PlayerUpdateService` / R2 index. */
export function isPlayerAppVersionDateBased(v: string): boolean {
  return /^\d{8}-\d+$/.test(v.trim())
}

/** True when `v` is valid semver (e.g. `1.0.0`). */
export function isPlayerAppVersionSemver(v: string): boolean {
  return valid(v.trim()) !== null
}

/**
 * True when `offered` and `client` use the **same** recognized scheme (both date-based or both semver),
 * or when either side is not a recognized date/semver token (defer to API index branch).
 * Used to refuse emitting a native APK update JWT when artifact and `x-player-version` schemes disagree
 * (e.g. date-based APK vs semver client), which would make the player treat the offer as always “newer” and loop.
 */
export function areNativeApkClientAndOfferVersionsComparable(offered: string, client: string): boolean {
  const o = offered.trim()
  const c = client.trim()
  const oDate = isPlayerAppVersionDateBased(o)
  const cDate = isPlayerAppVersionDateBased(c)
  const oSem = isPlayerAppVersionSemver(o)
  const cSem = isPlayerAppVersionSemver(c)
  const oKnown = oDate || oSem
  const cKnown = cDate || cSem
  if (!oKnown || !cKnown) {
    return true
  }
  if (oDate && cDate) {
    return true
  }
  if (oSem && cSem) {
    return true
  }
  return false
}

/**
 * True if `offered` (APK artifact version from the server) is strictly newer than `client`
 * (reported app version). Aligns with `PlayerUpdateService.checkForPlayerUpdate`.
 * When both sides are date-based or both semver, compares accordingly; otherwise returns **false**
 * (do not treat as newer — avoids download loops when schemes do not match; API should not emit those pairs).
 */
export function isNativeApkOfferedNewerThanClient(offered: string, client: string): boolean {
  const o = offered.trim()
  const c = client.trim()
  if (isPlayerAppVersionDateBased(c) && isPlayerAppVersionDateBased(o)) {
    return o > c
  }
  if (isPlayerAppVersionSemver(c) && isPlayerAppVersionSemver(o)) {
    return gt(o, c)
  }
  return false
}

/**
 * True when the client reports a version **at or past** the offered artifact (same scheme only).
 * Catches semver equality edge cases and date ordering; use before starting an APK download.
 */
export function isNativeApkClientAtOrNewerThanOffer(offered: string, client: string): boolean {
  const o = offered.trim()
  const c = client.trim()
  if (isPlayerAppVersionDateBased(o) && isPlayerAppVersionDateBased(c)) {
    return c >= o
  }
  if (isPlayerAppVersionSemver(o) && isPlayerAppVersionSemver(c)) {
    const ov = valid(o)
    const cv = valid(c)
    if (ov === null || cv === null) {
      return false
    }
    return gte(cv, ov)
  }
  return false
}

/**
 * PackageManager / crate version for APK OTA skip checks.
 * Empty/unknown shell must **not** be treated as current — a newer web OTA `package.json`
 * can otherwise skip installing a real APK (native 0.0.11 + web 1.0.0 → skip 1.0.0 APK).
 *
 * `null` = do not skip for version reasons (proceed if the server offered a JWT).
 */
export function shouldSkipAndroidApkOtaForNativeShell(opts: {
  offeredVersion: string
  nativeShellVersion: string | null | undefined
}): 'current' | 'incomparable' | null {
  const native = (opts.nativeShellVersion ?? '').trim()
  if (native === '') {
    return null
  }
  if (isNativeApkClientAtOrNewerThanOffer(opts.offeredVersion, native)) {
    return 'current'
  }
  if (!isNativeApkOfferedNewerThanClient(opts.offeredVersion, native)) {
    return 'incomparable'
  }
  return null
}

/**
 * `x-player-version` (handshake, REST, heartbeats). On Android, prefer the native shell so Web OTA
 * cannot advertise a newer semver than PackageManager (that blocked APK OTA).
 * Empty native + `preferNativeShell` must **not** fall back to embedded JS — that
 * inflates stored `players.version` and skips APK OTA.
 * Desktop / unknown native still max-merges so an older JS bundle cannot hide a newer APK.
 * Overlay **App v** uses {@link overlayInstalledPlayerAppVersion}, not this max-merge.
 */
export function resolvePlayerAppVersionForTelemetry(opts: {
  embedded: string
  nativeVersion: string | null | undefined
  preferNativeShell: boolean
}): string {
  const native = (opts.nativeVersion ?? '').trim()
  if (opts.preferNativeShell) {
    return native
  }
  return mergeEmbeddedAndNativePlayerAppVersion(opts.embedded, opts.nativeVersion)
}

/**
 * App version for Web OTA `minAppVersion` (handshake + registration-status).
 * Max-merges native `x-player-version`, optional JS `__APP_VERSION__`
 * (`x-player-embedded-version`), and stored `players.version`.
 * Do **not** use this for APK JWT compare — that stays native-header-only.
 */
export function resolveAppVersionForWebOtaMinAppVersion(opts: {
  headerVersion: string
  storedVersion?: string | null
  embeddedVersion?: string | null
}): string {
  const header = opts.headerVersion.trim()
  const stored = (opts.storedVersion ?? '').trim()
  const embedded = (opts.embeddedVersion ?? '').trim()
  let effective = header
  if (stored !== '') {
    effective = mergeEmbeddedAndNativePlayerAppVersion(header, stored)
  }
  if (embedded !== '') {
    effective = mergeEmbeddedAndNativePlayerAppVersion(embedded, effective)
  }
  return effective
}

/**
 * Merge embedded JS `__APP_VERSION__` with the native shell version (Tauri `app.getInfo().version`).
 * When both use the same scheme, returns the higher version. When schemes differ, prefers `nativeVersion`
 * so an older web OTA bundle cannot drag down the value reported to the API after a newer APK install.
 *
 * Do **not** use this max-merge as the APK OTA client version — see {@link shouldSkipAndroidApkOtaForNativeShell}.
 */
export function mergeEmbeddedAndNativePlayerAppVersion(embedded: string, nativeVersion: string | null | undefined): string {
  const e = embedded.trim()
  const n = (nativeVersion ?? '').trim()
  if (!n) {
    return e
  }
  if (!e) {
    return n
  }
  if (isPlayerAppVersionDateBased(e) && isPlayerAppVersionDateBased(n)) {
    return e > n ? e : n
  }
  if (isPlayerAppVersionSemver(e) && isPlayerAppVersionSemver(n)) {
    return gt(e, n) ? e : n
  }
  return n
}

/**
 * Persist reported `x-player-version` into `players.version`.
 * Android: incoming is PackageManager `versionName` — replace stored so a leftover Web OTA
 * semver (e.g. 1.0.1) cannot stick the admin App line on a 1.0.0 APK.
 * Other platforms: max-merge so an older JS bundle cannot hide a newer shell.
 */
export function mergeReportedPlayerAppVersion(opts: {
  prevStored: string | null | undefined
  incoming: string
  platform?: string | null
}): string {
  const prev = (opts.prevStored ?? '').trim()
  const inc = opts.incoming.trim()
  if (inc === '') {
    return prev
  }
  if ((opts.platform ?? '').trim() === 'android') {
    return inc
  }
  if (prev === '') {
    return inc
  }
  return mergeEmbeddedAndNativePlayerAppVersion(prev, inc)
}

/**
 * True when telemetry should report PackageManager / crate version instead of max(web, native).
 * `androidPackageVersionName` from the core `android_package_version` command is enough even
 * before `initPlatform()` sets `getPlatform()` to `'android'`.
 */
export function shouldPreferNativeShellPlayerAppVersion(opts: {
  platform: string
  androidPackageVersionName?: string | null
}): boolean {
  if (opts.platform === 'android') {
    return true
  }
  return (opts.androidPackageVersionName ?? '').trim() !== ''
}

/**
 * Overlay “App v” string: installed native shell (`versionName` / bundle), not merged Web OTA semver.
 * On web (`preferNativeShell` false) fall back to the heartbeat/embedded version.
 */
export function overlayInstalledPlayerAppVersion(opts: {
  nativeShellVersionName?: string | null
  appInfoVersion?: string | null
  mergedHeartbeatVersion?: string | null
  preferNativeShell: boolean
}): string | null {
  const native = (opts.nativeShellVersionName ?? '').trim()
  if (native) {
    return native
  }
  const info = (opts.appInfoVersion ?? '').trim()
  if (info) {
    return info
  }
  if (!opts.preferNativeShell) {
    const merged = (opts.mergedHeartbeatVersion ?? '').trim()
    return merged.length > 0 ? merged : null
  }
  return null
}
